"use client"
import { iconMap } from "@/app/(site)/_components/SanityGroups/HeadingGroup/helpers/iconMap"
import {
    FOCUS_VISIBLE_CLASS,
    FOCUS_VISIBLE_INSET_CLASS
} from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { type ButtonElementType } from "@/types/gc-modules-types"

const themeStyles: Partial<Record<ButtonElementType["theme"], string>> = {
    "primary-black": "bg-main-black text-main-white hover:bg-strong active:bg-strong",
    "secondary-black":
        "shadow-[inset_0_0_0_1px_theme(colors.main-black)] text-main-black bg-transparent hover:shadow-[inset_0_0_0_1px_theme(colors.border-neutral)] hover:text-secondary-text active:text-secondary-text",
    "secondary-white":
        "shadow-[inset_0_0_0_1px_theme(colors.main-white)] text-main-white bg-transparent hover:shadow-[inset_0_0_0_1px_theme(colors.border-neutral)] hover:text-main-light-grey-disabled",
    "tertiary-black":
        "bg-transparent text-black-main lg:hover:text-main-light-grey-disabled lg:active:text-main-light-grey-disabled",
    "tertiary-white": "bg-transparent text-white lg:hover:text-main-light-grey-disabled"
}

type ButtonKey = `${ButtonElementType["theme"]}-${ButtonElementType["size"]["desktop"]}`

const themeStylesPrimaryWhite: Partial<Record<ButtonKey, string>> = {
    "primary-white-small":
        "bg-main-white text-main-black hover:bg-muted active:bg-subtle",
    "primary-white-large":
        "bg-main-white text-main-black hover:bg-subtle active:bg-subtle"
}

const disabledStyles: Record<ButtonElementType["theme"], string> = {
    "primary-black": "bg-main-medium-grey opacity-40 text-main-light-grey-disabled",
    "primary-white": "bg-main-medium-grey opacity-40 text-main-light-grey-disabled",
    "secondary-black":
        "shadow-[inset_0_0_0_1px_theme(colors.main-light-grey-disabled)] opacity-40 text-main-light-grey-disabled",
    "secondary-white":
        "shadow-[inset_0_0_0_1px_theme(colors.main-light-grey-disabled)] opacity-40 text-main-light-grey-disabled",
    "tertiary-black": "opacity-40",
    "tertiary-white": "opacity-40"
}

export default function ButtonElement({
    text,
    size,
    theme,
    icon,
    iconPosition = "trailing",
    disabled,
    onClick,
    type = "button",
    className,
    duration = 250,
    removeFontCompensation = false,
    ...props
}: ButtonElementType & {
    onClick?: () => void
    type?: "button" | "submit" | "reset"
    duration?: number
}) {
    const isSmallDesktop = size.desktop === "small"

    const isPrimaryWhite = theme === "primary-white"

    const buttonClass = clsx(
        `flex w-fit h-40 items-center gap-x-8 transition-all duration-&lsqb;${duration}ms&rsqb; ease-out pt-18 pb-16 rounded-full`,
        isPrimaryWhite ? FOCUS_VISIBLE_INSET_CLASS : FOCUS_VISIBLE_CLASS,
        // Text-only buttons keep a flush left edge so the label stays aligned
        // with the surrounding copy; the right padding only grows the ring.
        theme.startsWith("tertiary") ? "pl-0 pr-20" : "px-20",
        disabled
            ? disabledStyles[theme]
            : isPrimaryWhite
              ? themeStylesPrimaryWhite[`${theme}-${size.desktop}`]
              : themeStyles[theme],
        disabled ? "pointer-events-none" : "cursor-pointer",
        isSmallDesktop
            ? "font_trimMonoPoc_Regular_14_120_uppercase"
            : "font_trimMonoPoc_Regular_14_120_uppercase md:font_trimMonoPoc_Regular_15_130_uppercase",
        className
    )

    const IconComponent = icon ? iconMap[icon] : null

    return (
        <button
            type={type}
            className={buttonClass}
            disabled={disabled}
            onClick={onClick}
            {...props}
        >
            {iconPosition === "leading" && IconComponent && (
                <IconComponent className="h-16 w-16" />
            )}
            <span className={clsx({ "pt-3": !removeFontCompensation })}>{text}</span>
            {iconPosition === "trailing" && IconComponent && (
                <IconComponent className="h-16 w-16" />
            )}
        </button>
    )
}
